import { useState, useEffect } from "react";
import { fetchWithTimeout } from "../../App";
import "../../App.css";

const API_URL = process.env.REACT_APP_API_URL;

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_URL}${url}`;
};

function Settings() {
  const [tab, setTab] = useState("qr");

  const [qrUrl, setQrUrl] = useState(null);
  const [qrFile, setQrFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrSuccess, setQrSuccess] = useState("");
  const [qrError, setQrError] = useState("");

  const [locations, setLocations] = useState([]);
  const [newLocName, setNewLocName] = useState("");
  const [newLocCategory, setNewLocCategory] = useState("Other Locations");
  const [newLocFee, setNewLocFee] = useState("");
  const [locError, setLocError] = useState("");
  const [editingLoc, setEditingLoc] = useState(null);

  const [waNumber, setWaNumber] = useState("");
  const [waSaving, setWaSaving] = useState(false);
  const [waSuccess, setWaSuccess] = useState("");
  const [waError, setWaError] = useState("");

  const [menuItems, setMenuItems] = useState([]);
  const [newItemName, setNewItemName] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemPoints, setNewItemPoints] = useState("3");
  const [menuError, setMenuError] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  const fetchQr = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/settings/duitnow_qr`);
      const data = await res.json();
      if (data.value) setQrUrl(data.value);
    } catch (err) { console.error("Error fetching QR:", err); }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/locations`);
      const data = await res.json();
      if (Array.isArray(data)) setLocations(data);
    } catch (err) { console.error("Error fetching locations:", err); }
  };

  const fetchWa = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/settings/whatsapp_number`);
      const data = await res.json();
      if (data.value) setWaNumber(data.value);
    } catch (err) { console.error("Error fetching WhatsApp:", err); }
  };

  const fetchMenuItems = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/menu`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setMenuItems(data);
    } catch (err) { console.error("Error fetching menu:", err); }
  };

  useEffect(() => {
    Promise.allSettled([fetchQr(), fetchLocations(), fetchWa(), fetchMenuItems()]);
  }, []);

  const createMenuItem = async (e) => {
    e.preventDefault();
    setMenuError("");
    if (!newItemName.trim()) return setMenuError("Name is required.");
    if (!newItemPrice || parseFloat(newItemPrice) <= 0) return setMenuError("Price must be greater than 0.");

    const res = await fetch(`${API_URL}/api/admin/menu`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: newItemName.trim(),
        price: parseFloat(newItemPrice),
        loyalty_points: Math.max(0, parseInt(newItemPoints || "0", 10) || 0),
      }),
    });
    const data = await res.json();
    if (!res.ok) return setMenuError(data.error || "Failed to create item.");
    setNewItemName("");
    setNewItemPrice("");
    setNewItemPoints("3");
    fetchMenuItems();
  };

  const saveMenuItemEdit = async (item) => {
    const res = await fetch(`${API_URL}/api/admin/menu/${item.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: item.name,
        price: item.price,
        available: item.available,
        loyalty_points: Math.max(0, parseInt(item.loyalty_points || 0, 10) || 0),
      }),
    });
    if (!res.ok) { const d = await res.json(); setMenuError(d.error || "Failed to update."); return; }
    setEditingItem(null);
    fetchMenuItems();
  };

  const toggleAvailable = async (item) => {
    await fetch(`${API_URL}/api/admin/menu/${item.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: item.name,
        price: item.price,
        available: !item.available,
        loyalty_points: Math.max(0, parseInt(item.loyalty_points || 0, 10) || 0),
      }),
    });
    fetchMenuItems();
  };

  const deleteMenuItem = async (id) => {
    if (!window.confirm("Delete this menu item? This cannot be undone.")) return;
    const res = await fetch(`${API_URL}/api/admin/menu/${id}`, {
      method: "DELETE", credentials: "include",
    });
    const data = await res.json();
    if (!res.ok) return setMenuError(data.error || "Failed to delete.");
    fetchMenuItems();
  };

  const saveWa = async (e) => {
    e.preventDefault();
    setWaError(""); setWaSuccess("");
    if (!waNumber.trim()) return setWaError("Please enter a WhatsApp number.");
    setWaSaving(true);
    const res = await fetch(`${API_URL}/api/admin/settings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ key: "whatsapp_number", value: waNumber.trim() }),
    });
    if (!res.ok) { setWaError("Failed to save."); setWaSaving(false); return; }
    setWaSuccess("✅ WhatsApp number saved!");
    setWaSaving(false);
  };

  const saveQr = async (e) => {
    e.preventDefault();
    setQrError(""); setQrSuccess("");
    if (!qrFile) return setQrError("Please select a QR image.");
    setSaving(true);

    const formData = new FormData();
    formData.append("proof", qrFile);

    try {
      const uploadRes = await fetch(`${API_URL}/api/admin/upload-qr`, {
        method: "POST", credentials: "include", body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok) { setQrError(uploadData.error || "Upload failed."); setSaving(false); return; }

      const saveRes = await fetch(`${API_URL}/api/admin/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ key: "duitnow_qr", value: uploadData.url }),
      });
      if (!saveRes.ok) { setQrError("Failed to save QR."); setSaving(false); return; }

      setQrUrl(uploadData.url);
      setQrFile(null);
      setQrSuccess("✅ QR saved! Students will see this on the payment page.");
    } catch (err) { setQrError("Something went wrong."); }
    setSaving(false);
  };

  const addLocation = async (e) => {
    e.preventDefault();
    setLocError("");
    if (!newLocName.trim()) return setLocError("Name is required.");
    if (newLocFee === "") return setLocError("Delivery fee is required.");

    const res = await fetch(`${API_URL}/api/admin/locations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: newLocName,
        category: newLocCategory,
        delivery_fee: parseFloat(newLocFee),
      }),
    });
    const data = await res.json();
    if (!res.ok) return setLocError(data.error);
    setNewLocName("");
    setNewLocCategory("Other Locations");
    setNewLocFee("");
    fetchLocations();
  };

  const saveLocEdit = async (loc) => {
    await fetch(`${API_URL}/api/admin/locations/${loc.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: loc.name,
        category: loc.category,
        delivery_fee: parseFloat(loc.delivery_fee),
      }),
    });
    setEditingLoc(null);
    fetchLocations();
  };

  const deleteLoc = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    await fetch(`${API_URL}/api/admin/locations/${id}`, {
      method: "DELETE", credentials: "include"
    });
    fetchLocations();
  };

  return (
    <div style={{ maxWidth: "600px" }}>
      <h1 className="admin-page-heading">⚙️ Settings</h1>
      <p className="admin-page-sub">Configure your kitchen</p>

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <button onClick={() => setTab("qr")} style={tab === "qr" ? activeTabStyle : tabStyle}>
          💳 DuitNow QR
        </button>
        <button onClick={() => setTab("locations")} style={tab === "locations" ? activeTabStyle : tabStyle}>
          📍 Delivery Locations
        </button>
        <button onClick={() => setTab("whatsapp")} style={tab === "whatsapp" ? activeTabStyle : tabStyle}>
          💬 WhatsApp
        </button>
        <button onClick={() => setTab("menu")} style={tab === "menu" ? activeTabStyle : tabStyle}>
          🍽️ Menu Items
        </button>
      </div>

      {tab === "qr" && (
        <div style={cardStyle}>
          <h2 style={sectionHeading}>💳 DuitNow QR Code</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.88rem", margin: "0 0 1.25rem" }}>
            Students will see this QR after placing an order when they need to pay.
          </p>

          {qrUrl && (
            <div style={{ marginBottom: "1.25rem", textAlign: "center" }}>
              <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>Current QR:</p>
              <img
                src={resolveUrl(qrUrl)}
                alt="DuitNow QR"
                style={{ maxWidth: "200px", border: "1px solid var(--border)", borderRadius: "12px", padding: "0.5rem" }}
              />
            </div>
          )}

          <form onSubmit={saveQr}>
            <label style={labelStyle}>{qrUrl ? "Replace QR" : "Upload QR image"}</label>
            <input
              type="file" accept="image/*"
              onChange={e => setQrFile(e.target.files[0])}
              style={{ display: "block", marginBottom: "1rem", fontSize: "0.9rem" }}
            />
            {qrFile && (
              <img src={URL.createObjectURL(qrFile)} alt="Preview"
                style={{ maxWidth: "160px", border: "1px solid var(--border)", borderRadius: "10px", marginBottom: "1rem" }} />
            )}
            {qrError && <p style={errorStyle}>{qrError}</p>}
            {qrSuccess && <p style={successStyle}>{qrSuccess}</p>}
            <button type="submit" disabled={saving} style={btnStyle}>
              {saving ? "Saving..." : "Save QR"}
            </button>
          </form>
        </div>
      )}

      {tab === "locations" && (
        <>
          <div style={cardStyle}>
            <h2 style={sectionHeading}>Add Location</h2>
            <form onSubmit={addLocation}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
                <div>
                  <label style={labelStyle}>Location Name</label>
                  <input
                    value={newLocName} onChange={e => setNewLocName(e.target.value)}
                    style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Category</label>
                  <input
                    value={newLocCategory}
                    onChange={e => setNewLocCategory(e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Residential Colleges"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Delivery Fee (RM)</label>
                  <input type="number" min="0" step="0.50"
                    value={newLocFee} onChange={e => setNewLocFee(e.target.value)}
                    style={inputStyle} />
                </div>
              </div>
              {locError && <p style={errorStyle}>{locError}</p>}
              <button type="submit" style={btnStyle}>+ Add Location</button>
            </form>
          </div>

          <div style={{ marginTop: "1rem" }}>
            {locations.length === 0 ? (
              <p style={{ color: "var(--text-muted)" }}>No locations yet.</p>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                    <th style={thStyle}>Location</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Delivery Fee</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.map(loc => (
                    <tr key={loc.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      {editingLoc?.id === loc.id ? (
                        <>
                          <td style={tdStyle}>
                            <input value={editingLoc.name}
                              onChange={e => setEditingLoc({ ...editingLoc, name: e.target.value })}
                              style={{ ...inputStyle, marginBottom: 0 }} />
                          </td>
                          <td style={tdStyle}>
                            <input value={editingLoc.category || ""}
                              onChange={e => setEditingLoc({ ...editingLoc, category: e.target.value })}
                              style={{ ...inputStyle, marginBottom: 0 }} />
                          </td>
                          <td style={tdStyle}>
                            <input type="number" min="0" step="0.50"
                              value={editingLoc.delivery_fee}
                              onChange={e => setEditingLoc({ ...editingLoc, delivery_fee: e.target.value })}
                              style={{ ...inputStyle, marginBottom: 0, width: "80px" }} />
                          </td>
                          <td style={tdStyle}>
                            <button onClick={() => saveLocEdit(editingLoc)} style={saveBtnStyle}>Save</button>
                            <button onClick={() => setEditingLoc(null)} style={{ ...cancelBtnStyle, marginLeft: "0.4rem" }}>✕</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={tdStyle}><strong>{loc.name}</strong></td>
                          <td style={tdStyle}>{loc.category || "Other Locations"}</td>
                          <td style={tdStyle}>
                            {parseFloat(loc.delivery_fee) === 0
                              ? <span style={{ color: "var(--success)" }}>Free</span>
                              : `RM ${parseFloat(loc.delivery_fee).toFixed(2)}`
                            }
                          </td>
                          <td style={tdStyle}>
                            <button onClick={() => setEditingLoc({ ...loc })} style={editBtnStyle}>✏️</button>
                            <button onClick={() => deleteLoc(loc.id)} style={{ ...deleteBtnStyle, marginLeft: "0.4rem" }}>🗑</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      {tab === "whatsapp" && (
        <div style={cardStyle}>
          <h2 style={sectionHeading}>💬 WhatsApp Contact</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.88rem", margin: "0 0 1.25rem" }}>
            Students will see a "Message me on WhatsApp" button on the Contact page.
          </p>
          <form onSubmit={saveWa}>
            <label style={labelStyle}>Your WhatsApp Number</label>
            <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: "0 0 0.5rem" }}>
              Include country code, no spaces or dashes. e.g. <strong>60123456789</strong> for Malaysia
            </p>
            <input
              value={waNumber}
              onChange={e => setWaNumber(e.target.value)}
              style={{ ...inputStyle, maxWidth: "280px", marginBottom: "1rem" }}
            />
            {waNumber && (
              <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", margin: "0 0 1rem" }}>
                Link preview:{" "}
                <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" style={{ color: "var(--orange)" }}>
                  wa.me/{waNumber}
                </a>
              </p>
            )}
            {waError && <p style={errorStyle}>{waError}</p>}
            {waSuccess && <p style={successStyle}>{waSuccess}</p>}
            <button type="submit" disabled={waSaving} style={btnStyle}>
              {waSaving ? "Saving..." : "Save Number"}
            </button>
          </form>
        </div>
      )}

      {tab === "menu" && (
        <div style={cardStyle}>
          <h2 style={sectionHeading}>🍽️ Menu Items</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.88rem", marginTop: 0 }}>
            Add, edit, or remove items from your menu.
          </p>

          <form onSubmit={createMenuItem} style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "1.5rem" }}>
            <div style={{ flex: 2, minWidth: "160px" }}>
              <label style={labelStyle}>Item Name</label>
              <input
                value={newItemName}
                onChange={e => setNewItemName(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: "120px" }}>
              <label style={labelStyle}>Price (RM)</label>
              <input
                type="number" min="0.01" step="0.01"
                value={newItemPrice}
                onChange={e => setNewItemPrice(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: "120px" }}>
              <label style={labelStyle}>Loyalty Points</label>
              <input
                type="number" min="0" step="1"
                value={newItemPoints}
                onChange={e => setNewItemPoints(e.target.value)}
                style={inputStyle}
              />
            </div>
            <button type="submit" style={saveBtnStyle}>+ Add Item</button>
          </form>

          {menuError && <p style={errorStyle}>{menuError}</p>}

          {menuItems.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>No menu items yet. Add your first item above.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {menuItems.map(item => (
                <div key={item.id} style={{
                  background: item.available ? "var(--warm-white)" : "var(--beige)",
                  border: `1px solid var(--border)`,
                  borderRadius: "10px",
                  padding: "0.85rem 1rem",
                  opacity: item.available ? 1 : 0.7,
                }}>
                  {editingItem?.id === item.id ? (
                    <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap" }}>
                      <div style={{ flex: 2, minWidth: "140px" }}>
                        <label style={labelStyle}>Name</label>
                        <input
                          value={editingItem.name}
                          onChange={e => setEditingItem({ ...editingItem, name: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: "100px" }}>
                        <label style={labelStyle}>Price (RM)</label>
                        <input
                          type="number" min="0.01" step="0.01"
                          value={editingItem.price}
                          onChange={e => setEditingItem({ ...editingItem, price: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: "110px" }}>
                        <label style={labelStyle}>Loyalty Points</label>
                        <input
                          type="number" min="0" step="1"
                          value={editingItem.loyalty_points ?? 0}
                          onChange={e => setEditingItem({ ...editingItem, loyalty_points: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                      <button onClick={() => saveMenuItemEdit(editingItem)} style={saveBtnStyle}>Save</button>
                      <button onClick={() => setEditingItem(null)} style={cancelBtnStyle}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: "bold", fontSize: "0.95rem", color: "var(--text-main)" }}>
                          {item.name}
                        </span>
                        <span style={{ marginLeft: "0.75rem", color: "var(--success)", fontWeight: "bold", fontSize: "0.9rem" }}>
                          RM {parseFloat(item.price).toFixed(2)}
                        </span>
                        <span style={{ marginLeft: "0.55rem", color: "var(--brown-mid)", fontWeight: 700, fontSize: "0.82rem" }}>
                          • {parseInt(item.loyalty_points || 0, 10)} pts
                        </span>
                        {!item.available && (
                          <span style={{ marginLeft: "0.6rem", fontSize: "0.78rem", background: "var(--beige)", color: "var(--text-muted)", padding: "0.15rem 0.5rem", borderRadius: "999px" }}>
                            Hidden
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          onClick={() => toggleAvailable(item)}
                          title={item.available ? "Hide from students" : "Show to students"}
                          style={{
                            background: item.available ? "#EDF5EE" : "var(--beige)",
                            border: `1px solid ${item.available ? "var(--success)" : "var(--border)"}`,
                            color: item.available ? "var(--success)" : "var(--text-muted)",
                            borderRadius: "8px", padding: "0.3rem 0.7rem",
                            fontSize: "0.82rem", cursor: "pointer",
                          }}
                        >
                          {item.available ? "✅ Available" : "🚫 Hidden"}
                        </button>
                        <button
                          onClick={() => setEditingItem({ ...item })}
                          style={editBtnStyle}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => deleteMenuItem(item.id)}
                          style={deleteBtnStyle}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const sectionHeading = { marginTop: 0, fontSize: "1.1rem", fontFamily: "'Playfair Display', Georgia, serif", color: "var(--text-main)" };
const cardStyle = { background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "16px", padding: "1.5rem", boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)" };
const tableStyle = { width: "100%", borderCollapse: "collapse", background: "var(--warm-white)", borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(92, 51, 23, 0.06)" };
const labelStyle = { display: "block", fontWeight: "600", fontSize: "0.85rem", color: "var(--brown-mid)", marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.04em" };
const inputStyle = { display: "block", width: "100%", padding: "0.6rem 0.85rem", marginBottom: "0", borderRadius: "10px", border: "1.5px solid var(--border)", background: "var(--beige)", fontSize: "0.95rem", boxSizing: "border-box", color: "var(--text-main)" };
const btnStyle = { background: "var(--orange)", color: "white", border: "none", padding: "0.65rem 1.5rem", borderRadius: "10px", fontSize: "1rem", fontWeight: "700", cursor: "pointer", boxShadow: "0 4px 14px rgba(224, 123, 57, 0.35)" };
const saveBtnStyle = { background: "var(--orange)", color: "white", border: "none", padding: "0.3rem 0.7rem", borderRadius: "8px", fontSize: "0.82rem", cursor: "pointer", fontWeight: "700" };
const cancelBtnStyle = { background: "transparent", border: "1px solid var(--border)", padding: "0.3rem 0.6rem", borderRadius: "8px", fontSize: "0.82rem", cursor: "pointer", color: "var(--text-muted)" };
const editBtnStyle = { background: "var(--beige)", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.3rem 0.7rem", fontSize: "0.82rem", cursor: "pointer" };
const deleteBtnStyle = { background: "#FDECEC", border: "1px solid #E8A8A8", borderRadius: "8px", padding: "0.3rem 0.6rem", fontSize: "0.82rem", cursor: "pointer", color: "var(--error)" };
const tabStyle = { background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "10px", padding: "0.5rem 1.25rem", fontSize: "0.95rem", cursor: "pointer", color: "var(--text-muted)" };
const activeTabStyle = { ...tabStyle, background: "var(--orange)", color: "white", border: "1px solid var(--orange)" };
const thStyle = { padding: "0.75rem 1rem", textAlign: "left", fontSize: "0.78rem", fontWeight: "700", color: "var(--brown-mid)", textTransform: "uppercase", letterSpacing: "0.04em" };
const tdStyle = { padding: "0.65rem 1rem", fontSize: "0.9rem", color: "var(--text-main)" };
const errorStyle = { color: "var(--error)", fontSize: "0.88rem", margin: "0.5rem 0" };
const successStyle = { color: "var(--success)", fontSize: "0.88rem", margin: "0.5rem 0" };

export default Settings;
